import { AttractionsList } from '@/components/attractions';
import { HotelsList } from '@/components/hotels';
import { LanguageSelector } from '@/components/tours/LanguageSelector';
import { TourAttractionThemeCell } from '@/components/tours/TourAttractionThemeCell';
import { TourTranslationsList } from '@/components/tours/translations';
import { attractionCrudConfig, type Attraction } from '@/config/objects/attractions';
import { hotelCrudConfig, type Hotel } from '@/config/objects/hotels';
import { tourCrudConfig, type Tour } from '@/config/objects/tours';
import { useTourWithLanguage } from '@/hooks/useTourWithLanguage';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CrudDetailView,
  PageContainer,
  useApiClient,
  type CrudConfig,
  type CrudDetailAction,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { GenerateVoiceoversAction, GenerateVoiceoversIcon } from './actions';

/**
 * Tour detail page
 */
export function TourDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [tour, setTour] = React.useState<Tour | null>(null);

  // Modal states
  const [generateVoiceoversModalOpen, setGenerateVoiceoversModalOpen] = useState(false);

  // Language selection state with localStorage persistence
  const [selectedLanguage, setSelectedLanguage] = useState<string>(() => {
    return localStorage.getItem('tour-preview-language') || 'en';
  });

  // Persist language selection
  useEffect(() => {
    localStorage.setItem('tour-preview-language', selectedLanguage);
  }, [selectedLanguage]);

  // Fetch tour with themed content based on selected language
  const {
    data: themedTour,
    isLoading: isLoadingThemedTour,
    error: themedTourError,
  } = useTourWithLanguage(id, selectedLanguage);

  /**
   * Delete tour mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (tourId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/tours/${tourId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        // Invalidate tours list to refresh data
        queryClient.invalidateQueries({ queryKey: ['Tour'] });
        navigate('/tours');
      } else {
        throw new Error(response.error || 'Failed to delete tour');
      }
    },
  });

  // Create custom config for tour-specific attractions
  const tourAttractionsConfig: CrudConfig<Attraction> = useMemo(
    () => ({
      ...attractionCrudConfig,
      entityName: 'Attraction',
      entityNamePlural: 'Attractions',
      endpoints: {
        ...attractionCrudConfig.endpoints,
        list: `/tours/${id}/attractions`,
        delete: (attractionId: string) => `/tours/${id}/attractions/${attractionId}`,
      },
      // Add custom theme field
      fields: [
        ...attractionCrudConfig.fields,
        {
          key: 'attractionTheme',
          label: 'Theme',
          type: 'custom',
          required: false,
          format: (_value: unknown, fullRecord?: Attraction) => {
            if (!fullRecord || !id) return null;
            return (
              <TourAttractionThemeCell
                tourId={id}
                attractionId={fullRecord.id}
                currentThemeId={fullRecord.tourAttraction?.attractionThemeId}
                currentThemeName={fullRecord.tourAttraction?.attractionTheme?.themeType?.name}
                themes={fullRecord.themes}
              />
            );
          },
        },
      ],
      listView: {
        ...attractionCrudConfig.listView,
        fields: [
          'featuredImage',
          'name',
          'attractionTheme',
          'status',
          'greaterArea',
          'neighborhood',
        ],
        actions: {
          add: {
            enabled: true,
            endpoint: '/attractions',
            // Filter attractions by tour's greater area and exclude already added attractions
            where: tour
              ? {
                  greaterAreaId: tour.greaterAreaId,
                  tours: {
                    none: {
                      tourId: tour.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (attraction: Attraction) => {
              await apiClient.post(`/tours/${id}/attractions`, { attractionId: attraction.id });
              toast.success('Attraction added to tour');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (attraction: Attraction) => {
              await apiClient.delete(`/tours/${id}/attractions/${attraction.id}`);
              toast.success('Attraction removed from tour');
            },
          },
        },
      },
    }),
    [tour, id, apiClient],
  );

  // Create custom config for tour-specific hotels
  const tourHotelsConfig: CrudConfig<Hotel> = useMemo(
    () => ({
      ...hotelCrudConfig,
      entityName: 'Hotel',
      entityNamePlural: 'Hotels',
      endpoints: {
        ...hotelCrudConfig.endpoints,
        list: `/tours/${id}/hotels`,
        delete: (hotelId: string) => `/tours/${id}/hotels/${hotelId}`,
      },
      listView: {
        ...hotelCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/hotels',
            // Filter hotels by tour's greater area and exclude already added hotels
            where: tour
              ? {
                  greaterAreaId: tour.greaterAreaId,
                  tours: {
                    none: {
                      tourId: tour.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (hotel: Hotel) => {
              await apiClient.post(`/tours/${id}/hotels`, { hotelId: hotel.id });
              toast.success('Hotel added to tour');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (hotel: Hotel) => {
              await apiClient.delete(`/tours/${id}/hotels/${hotel.id}`);
              toast.success('Hotel removed from tour');
            },
          },
        },
      },
    }),
    [tour, id, apiClient],
  );

  // Define custom detail actions
  const customActions: CrudDetailAction[] = useMemo(
    () => [
      {
        key: 'generate-voiceover',
        label: 'Generate Voiceover',
        icon: GenerateVoiceoversIcon,
        variant: 'secondary',
        onClick: () => setGenerateVoiceoversModalOpen(true),
      },
    ],
    [],
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Tour Not Found</h2>
          <p className="text-muted-foreground">The requested tour could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form for this tour
   */
  const handleEdit = (tour: Tour) => {
    navigate(`/tours/${tour.id}/edit`);
  };

  /**
   * Handle tour deletion
   */
  const handleDelete = async (tour: Tour) => {
    return toast.promise(deleteMutation.mutateAsync(tour.id), {
      loading: 'Deleting tour...',
      success: 'Tour deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete tour',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Tours', href: '/tours' },
    { label: tour?.name || 'Tour', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={tourCrudConfig}
          itemId={id}
          breadcrumbs={breadcrumbs}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onData={(tourData: Tour) => setTour(tourData)}
          customActions={customActions}
        />

        {/* Themed Content Preview Section */}
        {tour?.defaultThemeTypeId && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Themed Content Preview</CardTitle>
                  <CardDescription>
                    Preview how this tour appears with themed content in different languages
                  </CardDescription>
                </div>
                <LanguageSelector
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={setSelectedLanguage}
                />
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingThemedTour && (
                <div className="text-center py-8 text-muted-foreground">
                  Loading themed content...
                </div>
              )}

              {themedTourError && (
                <div className="text-center py-8 text-destructive">
                  Error loading themed content: {themedTourError.message}
                </div>
              )}

              {themedTour && !isLoadingThemedTour && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Tour Name</h3>
                    <p className="text-lg font-semibold">{themedTour.name}</p>
                  </div>

                  {themedTour.description && (
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-2">
                        Description
                      </h3>
                      <p className="text-sm">{themedTour.description}</p>
                    </div>
                  )}

                  {themedTour.attractions && themedTour.attractions.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-3">
                        Themed Attractions ({themedTour.attractions.length})
                      </h3>
                      <div className="space-y-3">
                        {themedTour.attractions.map((attraction, index: number) => (
                          <div key={attraction.id} className="border rounded-lg p-4 bg-muted/30">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                                {index + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium mb-1">{attraction.name}</h4>
                                {attraction.description && (
                                  <p className="text-sm text-muted-foreground line-clamp-2">
                                    {attraction.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tour Attractions Section */}
        <AttractionsList config={tourAttractionsConfig} className="p-0" baseUrl="/attractions" />

        {/* Tour Hotels Section */}
        <HotelsList config={tourHotelsConfig} className="p-0" baseUrl="/hotels" />

        {/* Translations Section */}
        {id && <TourTranslationsList tourId={id} className="p-0" />}

        {/* Action Modals */}
        <GenerateVoiceoversAction
          tourId={id}
          open={generateVoiceoversModalOpen}
          onOpenChange={setGenerateVoiceoversModalOpen}
        />
      </div>
    </PageContainer>
  );
}
